import { useCallback, useEffect, useRef, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";

import { PageHeader } from "../../../shared/components/PageHeader";
import { GoogleSheetCreateCard, GoogleSheetList } from "../components/GoogleSheetsManagement";
import {
  getProposeTemplate,
  getProposeTemplateErrorMessage,
  getProposeTemplatePreview,
  getProposeTemplateStatus,
  listProposeTemplates,
  deleteProposeTemplate,
  renameProposeTemplate,
  updateProposeTemplateCells,
  uploadProposeTemplate,
  type ProposeTemplate,
  type ProposeTemplateCellEdit,
  type ProposeTemplateField,
  type ProposeTemplateFormDefinition,
  type ProposeTemplateInitialProduct,
  type ProposeTemplatePreview,
  type ProposeTemplatePreviewSheet,
  type ProposeTemplateRepeatableGroup,
  type ProposeTemplateTable,
} from "../api/proposeTemplatesApi";

const { Text } = Typography;

type TemplateProductRow = {
  key: string;
  tableId: string;
  groupIds?: Array<string | number>;
  values: Record<string, unknown>;
};

type TemplateGroupInstance = {
  key: string;
  groupId: string;
  externalId?: string;
  initial: boolean;
  values: Record<string, unknown>;
  children: TemplateGroupInstance[];
};

type TemplateCellEdits = Record<string, ProposeTemplateCellEdit>;

const statusLabel = (status: ProposeTemplate["status"]) => {
  if (status === 1) return <Tag color="success">準備完了</Tag>;
  if (status === -1) return <Tag color="error">解析失敗</Tag>;
  return <Tag color="processing">解析中</Tag>;
};

const displayValue = (value: unknown) =>
  value === null || value === undefined ? "" : String(value);

const initialProductGroupId = (
  groupIds: ProposeTemplateInitialProduct["groupIds"],
  index: number,
  groupId: string,
) => {
  if (Array.isArray(groupIds)) return groupIds[index];
  if (groupIds && typeof groupIds === "object") {
    return groupIds[groupId] ?? Object.values(groupIds)[index];
  }
  return undefined;
};

const toSelectOptions = (options: unknown[] = []) =>
  options.map((option) => {
    if (typeof option === "string" || typeof option === "number") {
      return { value: option, label: String(option) };
    }
    if (option && typeof option === "object") {
      const record = option as Record<string, unknown>;
      const value = record.value ?? record.id ?? record.label ?? "";
      return {
        value: String(value),
        label: String(record.label ?? record.name ?? value),
      };
    }
    return { value: String(option), label: String(option) };
  });

const TemplateFieldInput = ({
  field,
  value,
  onChange,
}: {
  field: ProposeTemplateField;
  value: unknown;
  onChange: (next: unknown) => void;
}) => {
  const disabled = Boolean(field.readOnly || field.calculated);
  const inputType = field.inputType?.toLowerCase();

  if (inputType === "checkbox" || inputType === "boolean") {
    return (
      <Checkbox
        checked={Boolean(value)}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
    );
  }
  if (inputType === "select" || inputType === "dropdown") {
    return (
      <Select
        allowClear
        disabled={disabled}
        value={value === "" || value == null ? undefined : String(value)}
        options={toSelectOptions(field.options)}
        onChange={onChange}
      />
    );
  }
  if (inputType === "textarea" || inputType === "multiline") {
    return (
      <Input.TextArea
        value={displayValue(value)}
        disabled={disabled}
        autoSize={{ minRows: 2, maxRows: 5 }}
        onChange={(event) => onChange(event.target.value)}
      />
    );
  }
  return (
    <Input
      type={inputType === "number" || inputType === "date" ? inputType : "text"}
      value={displayValue(value)}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
    />
  );
};

const createFieldValues = (fields: ProposeTemplateField[]) =>
  fields.reduce<Record<string, unknown>>((values, field) => {
    values[field.id] = field.defaultValue ?? "";
    return values;
  }, {});

const templateTables = (
  definition: ProposeTemplateFormDefinition,
): ProposeTemplateTable[] => {
  if (definition.tables?.length) return definition.tables;
  return [
    {
      id: "products",
      label: "商品",
      fieldIds: (definition.productFields ?? []).map((field) => field.id),
      groupPath: [],
    },
  ];
};

const initializeRows = (definition: ProposeTemplateFormDefinition) =>
  (definition.initialProducts ?? []).map((product, index) => {
    const groupPath =
      definition.tables?.find((table) => table.id === product.tableId)?.groupPath ?? [];
    return {
      key: `${product.tableId}-${product.sourceRow ?? index}-${index}`,
      tableId: product.tableId,
      groupIds: groupPath.map((groupId, groupIndex) =>
        initialProductGroupId(product.groupIds, groupIndex, groupId) ?? "",
      ),
      values: { ...product.values },
    };
  });

const makeGroupInstance = (
  group: ProposeTemplateRepeatableGroup,
  keyPrefix: string,
  externalId?: string,
  initial = false,
): TemplateGroupInstance => ({
  key: `${keyPrefix}/${externalId ?? `new-${Math.random().toString(36).slice(2)}`}`,
  groupId: group.id,
  externalId,
  initial,
  values: createFieldValues(group.fields ?? []),
  children: (group.children ?? []).map((child) =>
    makeGroupInstance(child, `${keyPrefix}/${externalId ?? group.id}`, undefined, initial),
  ),
});

const initializeGroupInstances = (
  groups: ProposeTemplateRepeatableGroup[],
  definition: ProposeTemplateFormDefinition,
  parentPath: string[] = [],
  parentExternalIds: Array<string | undefined> = [],
): TemplateGroupInstance[] =>
  groups.flatMap((group) => {
    const currentPath = [...parentPath, group.id];
    const tableIds = new Set(
      (definition.tables ?? [])
        .filter((table) =>
          currentPath.every((groupId, index) => table.groupPath?.[index] === groupId),
        )
        .map((table) => table.id),
    );
    const groupIndex = currentPath.length - 1;
    const externalIds = Array.from(
      new Set(
        (definition.initialProducts ?? [])
          .filter(
            (product) =>
              tableIds.has(product.tableId) &&
              parentExternalIds.every((parentId, index) => {
                const productGroupId = initialProductGroupId(
                  product.groupIds,
                  index,
                  parentPath[index],
                );
                return (
                  productGroupId === undefined ||
                  parentId === undefined ||
                  String(productGroupId) === String(parentId)
                );
              }),
          )
          .map((product) =>
            initialProductGroupId(product.groupIds, groupIndex, group.id),
          )
          .filter((id): id is string | number => id !== undefined && id !== null)
          .map(String),
      ),
    );
    const ids: Array<string | undefined> = externalIds.length
      ? externalIds
      : [undefined];
    return ids.map((externalId, index) => {
      const instance = makeGroupInstance(
        group,
        `initial-${currentPath.join("-")}-${index}`,
        externalId,
        true,
      );
      instance.children = initializeGroupInstances(
        group.children ?? [],
        definition,
        currentPath,
        [...parentExternalIds, externalId],
      ).map((child) => ({
        ...child,
        key: `${instance.key}/${child.key}`,
      }));
      return instance;
    });
  });

const assignInitialGroupIds = (
  rows: TemplateProductRow[],
  definition: ProposeTemplateFormDefinition,
  groups: TemplateGroupInstance[],
) =>
  rows.map((row) => {
    const path = definition.tables?.find((table) => table.id === row.tableId)?.groupPath ?? [];
    if (!path.length) return row;
    let currentGroups = groups;
    const groupIds = path.map((groupId, index) => {
      const rowId = row.groupIds?.[index];
      const hasRowId = rowId !== undefined && rowId !== null && rowId !== "";
      const instance = currentGroups.find(
        (group) =>
          group.groupId === groupId &&
          (!hasRowId || String(group.externalId) === String(rowId)),
      ) ?? currentGroups.find((group) => group.groupId === groupId);
      if (!instance) return hasRowId ? rowId : "";
      currentGroups = instance.children;
      return (hasRowId ? rowId : undefined) ?? instance.externalId ?? instance.key;
    });
    return { ...row, groupIds };
  });

const updateGroupInstances = (
  groups: TemplateGroupInstance[],
  key: string,
  update: (group: TemplateGroupInstance) => TemplateGroupInstance,
): TemplateGroupInstance[] =>
  groups.map((group) => {
    if (group.key === key) return update(group);
    return {
      ...group,
      children: updateGroupInstances(group.children, key, update),
    };
  });

const removeGroupInstance = (
  groups: TemplateGroupInstance[],
  key: string,
): TemplateGroupInstance[] =>
  groups
    .filter((group) => group.key !== key)
    .map((group) => ({
      ...group,
      children: removeGroupInstance(group.children, key),
    }));

const groupInstanceKeys = (groups: TemplateGroupInstance[]): string[] =>
  groups.flatMap((group) => [group.key, ...groupInstanceKeys(group.children)]);

const columnLabel = (column: number) => {
  let remaining = column;
  let label = "";
  while (remaining > 0) {
    const remainder = (remaining - 1) % 26;
    label = String.fromCharCode(65 + remainder) + label;
    remaining = Math.floor((remaining - 1) / 26);
  }
  return label;
};

const cellPosition = (address: string) => {
  const match = address.match(/^([A-Z]+)(\d+)$/i);
  if (!match) return null;
  const column = match[1]
    .toUpperCase()
    .split("")
    .reduce((number, letter) => number * 26 + letter.charCodeAt(0) - 64, 0);
  return { row: Number(match[2]), column };
};

const mergeBounds = (merge: string) => {
  const [startAddress, endAddress] = merge.split(":");
  if (!startAddress || !endAddress) return null;
  const start = cellPosition(startAddress);
  const end = cellPosition(endAddress);
  if (!start || !end) return null;
  return {
    ...start,
    rowSpan: Math.max(1, end.row - start.row + 1),
    columnSpan: Math.max(1, end.column - start.column + 1),
    endRow: end.row,
    endColumn: end.column,
  };
};

export const TemplateSheetPreview = ({
  sheet,
  edits,
  onCellChange,
  readOnly = false,
}: {
  sheet: ProposeTemplatePreviewSheet;
  edits?: TemplateCellEdits;
  onCellChange?: (sheetName: string, address: string, value: string) => void;
  readOnly?: boolean;
}) => {
  const [activeAddress, setActiveAddress] = useState("A1");
  const mergedTops = new Map<
    string,
    { rowSpan: number; columnSpan: number }
  >();
  const coveredCells = new Set<string>();
  (sheet.merges ?? []).forEach((merge) => {
    const bounds = mergeBounds(merge);
    if (!bounds) return;
    mergedTops.set(`${bounds.row}:${bounds.column}`, {
      rowSpan: bounds.rowSpan,
      columnSpan: bounds.columnSpan,
    });
    for (let row = bounds.row; row <= bounds.endRow; row += 1) {
      for (let column = bounds.column; column <= bounds.endColumn; column += 1) {
        if (row !== bounds.row || column !== bounds.column) {
          coveredCells.add(`${row}:${column}`);
        }
      }
    }
  });

  const activeValue =
    edits?.[`${sheet.name}!${activeAddress}`]?.value ??
    sheet.cells[activeAddress] ??
    "";

  return (
    <div style={workbookFrameStyle}>
      <div style={formulaBarStyle}>
        <div style={nameBoxStyle}>{activeAddress}</div>
        <div style={formulaSymbolStyle}>{sheet.formulas?.[activeAddress] ? "fx" : "="}</div>
        <div style={formulaValueStyle}>
          {sheet.formulas?.[activeAddress]
            ? `=${sheet.formulas[activeAddress]}`
            : String(activeValue)}
        </div>
      </div>
      <div style={{ overflow: "auto", maxHeight: 560 }}>
      <table
        style={{
          borderCollapse: "collapse",
          width: "max-content",
          minWidth: "100%",
          tableLayout: "fixed",
        }}
      >
        <thead>
          <tr>
            <th style={{ ...previewHeaderCellStyle, ...rowNumberHeaderStyle }}>#</th>
            {Array.from({ length: sheet.maxColumn }, (_, index) => (
              <th key={index} style={{ ...previewHeaderCellStyle, ...columnHeaderStyle }}>
                {columnLabel(index + 1)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: sheet.maxRow }, (_, rowIndex) => {
            const rowNumber = rowIndex + 1;
            return (
              <tr key={rowNumber}>
                <th style={{ ...previewHeaderCellStyle, ...rowNumberHeaderStyle }}>{rowNumber}</th>
                {Array.from({ length: sheet.maxColumn }, (_, columnIndex) => {
                  const columnNumber = columnIndex + 1;
                  const cellKey = `${rowNumber}:${columnNumber}`;
                  if (coveredCells.has(cellKey)) return null;
                  const address = `${columnLabel(columnNumber)}${rowNumber}`;
                  const editKey = `${sheet.name}!${address}`;
                  const displayed = String(sheet.cells[address] ?? "");
                  const merged = mergedTops.get(cellKey);
                  return (
                    <td
                      key={address}
                      colSpan={merged?.columnSpan}
                      rowSpan={merged?.rowSpan}
                      style={{
                        ...previewCellStyle,
                        ...(activeAddress === address ? activeCellStyle : {}),
                      }}
                    >
                      {readOnly ? (
                        <div
                          aria-label={`${sheet.name} ${address}`}
                          onClick={() => setActiveAddress(address)}
                          style={{ ...spreadsheetInputStyle, cursor: "default" }}
                        >
                          {String(edits?.[editKey]?.value ?? displayed)}
                        </div>
                      ) : (
                        <Input
                          size="small"
                          aria-label={`${sheet.name} ${address}`}
                          onFocus={() => setActiveAddress(address)}
                          value={edits?.[editKey]?.value ?? displayed}
                          onChange={(event) =>
                            onCellChange?.(sheet.name, address, event.target.value)
                          }
                          style={spreadsheetInputStyle}
                        />
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
      <div style={sheetTabRailStyle}>
        <div style={activeSheetTabStyle}>{sheet.name}</div>
      </div>
    </div>
  );
};

const previewHeaderCellStyle: CSSProperties = {
  border: "1px solid #d5d9d6",
  background: "#f3f5f4",
  padding: "4px 8px",
  minWidth: 44,
  fontWeight: 500,
  textAlign: "center",
  color: "#56615b",
  position: "sticky",
  top: 0,
  zIndex: 2,
};

const previewCellStyle: CSSProperties = {
  border: "1px solid #e1e5e2",
  padding: 0,
  verticalAlign: "top",
  background: "white",
  minWidth: 112,
  height: 30,
};

const workbookFrameStyle: CSSProperties = {
  border: "1px solid #cbd2ce",
  borderRadius: 6,
  overflow: "hidden",
  background: "#fff",
};

const formulaBarStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "8px 10px",
  borderBottom: "1px solid #d5d9d6",
  background: "#f8f9f8",
  color: "#343a36",
  fontSize: 13,
};

const nameBoxStyle: CSSProperties = {
  width: 70,
  padding: "4px 8px",
  border: "1px solid #cbd2ce",
  borderRadius: 3,
  background: "white",
  fontWeight: 600,
};

const formulaSymbolStyle: CSSProperties = {
  width: 28,
  textAlign: "center",
  color: "#68736d",
  fontStyle: "italic",
};

const formulaValueStyle: CSSProperties = {
  flex: 1,
  minHeight: 26,
  padding: "4px 8px",
  border: "1px solid #cbd2ce",
  borderRadius: 3,
  background: "white",
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
};

const rowNumberHeaderStyle: CSSProperties = {
  minWidth: 48,
  width: 48,
  left: 0,
  zIndex: 3,
};

const columnHeaderStyle: CSSProperties = {
  minWidth: 112,
  width: 112,
};

const activeCellStyle: CSSProperties = {
  outline: "2px solid #217346",
  outlineOffset: -2,
  position: "relative",
  zIndex: 1,
};

const spreadsheetInputStyle: CSSProperties = {
  width: "100%",
  minWidth: 112,
  height: 29,
  padding: "4px 7px",
  border: "none",
  borderRadius: 0,
  boxShadow: "none",
  color: "#202722",
  background: "transparent",
};

const sheetTabRailStyle: CSSProperties = {
  display: "flex",
  alignItems: "end",
  minHeight: 36,
  padding: "5px 12px 0",
  borderTop: "1px solid #d5d9d6",
  background: "#f3f5f4",
};

const activeSheetTabStyle: CSSProperties = {
  padding: "6px 16px 7px",
  border: "1px solid #d5d9d6",
  borderBottom: "2px solid #217346",
  borderRadius: "4px 4px 0 0",
  background: "white",
  color: "#217346",
  fontSize: 12,
  fontWeight: 600,
};

export const ProposeTemplateManagementPage = () => {
  const [googleSheetsRefreshKey, setGoogleSheetsRefreshKey] = useState(0);
  const [templates, setTemplates] = useState<ProposeTemplate[]>([]);
  const [name, setName] = useState("");
  const [nameEdited, setNameEdited] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [listLoading, setListLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [renamingTemplate, setRenamingTemplate] = useState<ProposeTemplate | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [renameSaving, setRenameSaving] = useState(false);
  const [renameError, setRenameError] = useState<string | null>(null);
  const [previewId, setPreviewId] = useState<number | null>(null);
  const [preview, setPreview] = useState<ProposeTemplatePreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewSaving, setPreviewSaving] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [cellEdits, setCellEdits] = useState<TemplateCellEdits>({});
  const [templateForm, setTemplateForm] =
    useState<ProposeTemplateFormDefinition | null>(null);
  const [headerValues, setHeaderValues] = useState<Record<string, unknown>>({});
  const [productRows, setProductRows] = useState<TemplateProductRow[]>([]);
  const [groupInstances, setGroupInstances] = useState<TemplateGroupInstance[]>([]);
  const [formLoading, setFormLoading] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);
  const [pageSuccess, setPageSuccess] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [templateErrors, setTemplateErrors] = useState<Record<number, string>>({});
  const pollingTimers = useRef(new Map<number, number | null>());
  const failedStatusLoaded = useRef(new Set<number>());

  const refreshGoogleSheetTemplateList = async () => {
    setListLoading(true);
    setGoogleSheetsRefreshKey((key) => key + 1);
    try {
      setTemplates(await listProposeTemplates());
    } catch (error) {
      setPageError(getProposeTemplateErrorMessage(error));
    } finally {
      setListLoading(false);
    }
  };

  const handleGoogleSheetTemplateSynced = async (
    templateId: number,
    form: ProposeTemplateFormDefinition,
  ) => {
    await refreshGoogleSheetTemplateList();
    if (selectedId !== templateId) return;

    setTemplateForm(form);
    setHeaderValues(createFieldValues(form.headerFields ?? []));
    const initialGroups = initializeGroupInstances(form.repeatableGroups ?? [], form);
    setGroupInstances(initialGroups);
    setProductRows(assignInitialGroupIds(initializeRows(form), form, initialGroups));
    setFormError(null);
  };

  const pollStatus = useCallback(async (id: number) => {
    try {
      const result = await getProposeTemplateStatus(id);
      setTemplates((current) =>
        current.map((template) =>
          template.id === id ? { ...template, status: result.status } : template,
        ),
      );
      if (result.status === 0) {
        const timer = window.setTimeout(() => void pollStatus(id), 2000);
        pollingTimers.current.set(id, timer);
        setTemplateErrors((current) => {
          if (!current[id]) return current;
          const next = { ...current };
          delete next[id];
          return next;
        });
      } else {
        pollingTimers.current.delete(id);
        if (result.status === -1) {
          setTemplateErrors((current) => ({
            ...current,
            [id]: result.error || "テンプレートの解析に失敗しました。",
          }));
        } else {
          setTemplateErrors((current) => {
            if (!current[id]) return current;
            const next = { ...current };
            delete next[id];
            return next;
          });
        }
      }
    } catch (error) {
      setTemplateErrors((current) => ({
        ...current,
        [id]: getProposeTemplateErrorMessage(error),
      }));
      const timer = window.setTimeout(() => void pollStatus(id), 5000);
      pollingTimers.current.set(id, timer);
    }
  }, []);

  const startPolling = useCallback(
    (id: number) => {
      if (pollingTimers.current.has(id)) return;
      pollingTimers.current.set(id, null);
      void pollStatus(id);
    },
    [pollStatus],
  );

  useEffect(() => {
    let active = true;
    listProposeTemplates()
      .then((result) => {
        if (active) setTemplates(result);
      })
      .catch((error) => {
        if (active) setPageError(getProposeTemplateErrorMessage(error));
      })
      .finally(() => {
        if (active) setListLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    templates
      .filter((template) => template.status === 0)
      .forEach((template) => startPolling(template.id));
  }, [templates, startPolling]);

  useEffect(() => {
    templates
      .filter(
        (template) =>
          template.status === -1 && !failedStatusLoaded.current.has(template.id),
      )
      .forEach((template) => {
        failedStatusLoaded.current.add(template.id);
        getProposeTemplateStatus(template.id)
          .then((result) => {
            if (result.error) {
              setTemplateErrors((current) => ({
                ...current,
                [template.id]: result.error as string,
              }));
            }
          })
          .catch((error) => {
            setTemplateErrors((current) => ({
              ...current,
              [template.id]: getProposeTemplateErrorMessage(error),
            }));
          });
      });
  }, [templates]);

  useEffect(
    () => () => {
      pollingTimers.current.forEach((timer) => {
        if (timer !== null) window.clearTimeout(timer);
      });
      pollingTimers.current.clear();
    },
    [],
  );

  useEffect(() => {
    if (selectedId === null) {
      setTemplateForm(null);
      setHeaderValues({});
      setProductRows([]);
      setGroupInstances([]);
      setFormError(null);
      return;
    }
    let active = true;
    setTemplateForm(null);
    setHeaderValues({});
    setProductRows([]);
    setGroupInstances([]);
    setGroupInstances([]);
    setFormError(null);
    setFormLoading(true);
    getProposeTemplate(selectedId)
      .then((result) => {
        if (!active) return;
        setTemplateForm(result);
        setHeaderValues(createFieldValues(result.headerFields ?? []));
        const initialGroups = initializeGroupInstances(
          result.repeatableGroups ?? [],
          result,
        );
        setGroupInstances(initialGroups);
        setProductRows(
          assignInitialGroupIds(initializeRows(result), result, initialGroups),
        );
      })
      .catch((error) => {
        if (active) setFormError(getProposeTemplateErrorMessage(error));
      })
      .finally(() => {
        if (active) setFormLoading(false);
      });
    return () => {
      active = false;
    };
  }, [selectedId]);

  useEffect(() => {
    if (previewId === null) {
      setPreview(null);
      setCellEdits({});
      setPreviewError(null);
      return;
    }
    let active = true;
    setPreview(null);
    setCellEdits({});
    setPreviewError(null);
    setPreviewLoading(true);
    getProposeTemplatePreview(previewId)
      .then((result) => {
        if (active) setPreview(result);
      })
      .catch((error) => {
        if (active) setPreviewError(getProposeTemplateErrorMessage(error));
      })
      .finally(() => {
        if (active) setPreviewLoading(false);
      });
    return () => {
      active = false;
    };
  }, [previewId]);

  const handleUpload = async () => {
    if (!file || !name.trim()) return;
    if (!file.name.toLowerCase().endsWith(".xlsx")) {
      setPageError(".xlsx形式のExcelファイルを選択してください。");
      return;
    }
    setUploading(true);
    setPageError(null);
    try {
      const uploaded = await uploadProposeTemplate(file, name.trim());
      setTemplates((current) => [
        uploaded,
        ...current.filter((template) => template.id !== uploaded.id),
      ]);
      setName("");
      setNameEdited(false);
      setFile(null);
      const fileInput = document.getElementById(
        "propose-template-file",
      ) as HTMLInputElement | null;
      if (fileInput) fileInput.value = "";
    } catch (error) {
      setPageError(getProposeTemplateErrorMessage(error));
    } finally {
      setUploading(false);
    }
  };

  const handlePreviewCellChange = (
    sheetName: string,
    address: string,
    value: string,
  ) => {
    const sheet = preview?.sheets.find((item) => item.name === sheetName);
    const original = String(sheet?.cells[address] ?? "");
    const key = `${sheetName}!${address}`;
    setCellEdits((current) => {
      if (value === original) {
        if (!current[key]) return current;
        const next = { ...current };
        delete next[key];
        return next;
      }
      return {
        ...current,
        [key]: { sheetName, address, value },
      };
    });
  };

  const savePreviewEdits = async () => {
    if (previewId === null || !Object.keys(cellEdits).length) return;
    setPreviewSaving(true);
    setPreviewError(null);
    try {
      const updated = await updateProposeTemplateCells(
        previewId,
        Object.values(cellEdits),
      );
      setPreview(updated);
      setCellEdits({});
    } catch (error) {
      setPreviewError(getProposeTemplateErrorMessage(error));
    } finally {
      setPreviewSaving(false);
    }
  };

  const handleDeleteTemplate = async (template: ProposeTemplate) => {
    setPageError(null);
    try {
      await deleteProposeTemplate(template.id);
      setTemplates((current) =>
        current.filter((item) => item.id !== template.id),
      );
      if (selectedId === template.id) setSelectedId(null);
      if (previewId === template.id) setPreviewId(null);
    } catch (error) {
      setPageError(getProposeTemplateErrorMessage(error));
    }
  };

  const handleRenameTemplate = async () => {
    if (!renamingTemplate) return;
    const name = renameValue.trim();
    if (!name) {
      setRenameError("テンプレート名を入力してください。");
      return;
    }

    setRenameSaving(true);
    setRenameError(null);
    try {
      const result = await renameProposeTemplate(renamingTemplate.id, name);
      if (!result.success) {
        setRenameError(result.detail || "テンプレート名を変更できませんでした。");
        return;
      }

      setTemplates((current) =>
        current.map((template) =>
          template.id === renamingTemplate.id ? { ...template, name } : template,
        ),
      );
      setPreview((current) =>
        current?.templateId === renamingTemplate.id ? { ...current, name } : current,
      );
      setGoogleSheetsRefreshKey((key) => key + 1);
      setPageSuccess(result.detail || "Template name updated.");
      setRenamingTemplate(null);
    } catch (error) {
      setRenameError(getProposeTemplateErrorMessage(error));
    } finally {
      setRenameSaving(false);
    }
  };

  const handleGoogleSheetRenamed = (
    templateId: number,
    renamedName: string,
    detail: string,
  ) => {
    setTemplates((current) =>
      current.map((template) =>
        template.id === templateId ? { ...template, name: renamedName } : template,
      ),
    );
    setPreview((current) =>
      current?.templateId === templateId ? { ...current, name: renamedName } : current,
    );
    setGoogleSheetsRefreshKey((key) => key + 1);
    setPageSuccess(detail || "Template name updated.");
  };

  const tables = templateForm ? templateTables(templateForm) : [];
  const productFields = templateForm?.productFields ?? [];

  const updateProductValue = (
    key: string,
    fieldId: string,
    value: unknown,
  ) => {
    setProductRows((current) =>
      current.map((row) =>
        row.key === key
          ? { ...row, values: { ...row.values, [fieldId]: value } }
          : row,
      ),
    );
  };

  const addProductRow = (
    table: ProposeTemplateTable,
    pathInstances: TemplateGroupInstance[] = [],
  ) => {
    const tableFields = productFields.filter(
      (field) => !table.fieldIds?.length || table.fieldIds.includes(field.id),
    );
    setProductRows((current) => [
      ...current,
      {
        key: `${table.id}-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        tableId: table.id,
        groupIds: pathInstances.map((group) => group.externalId ?? group.key),
        values: createFieldValues(tableFields),
      },
    ]);
  };

  const updateGroupValue = (
    key: string,
    field: ProposeTemplateField,
    value: unknown,
  ) => {
    if (field.headerFieldId) {
      setHeaderValues((current) => ({
        ...current,
        [field.headerFieldId as string]: value,
      }));
      return;
    }
    setGroupInstances((current) =>
      updateGroupInstances(current, key, (group) => ({
        ...group,
        values: { ...group.values, [field.id]: value },
      })),
    );
  };

  const addGroup = (
    parentKey: string | null,
    definition: ProposeTemplateRepeatableGroup,
  ) => {
    const instance = makeGroupInstance(
      definition,
      parentKey ?? `new-${definition.id}`,
    );
    if (parentKey === null) {
      setGroupInstances((current) => [...current, instance]);
      return;
    }
    setGroupInstances((current) =>
      updateGroupInstances(current, parentKey, (group) => ({
        ...group,
        children: [...group.children, instance],
      })),
    );
  };

  const deleteGroup = (key: string) => {
    const allKeys = new Set<string>();
    const findDeleted = (groups: TemplateGroupInstance[]): void => {
      for (const group of groups) {
        if (group.key === key) {
          groupInstanceKeys([group]).forEach((groupKey) => allKeys.add(groupKey));
          const collectExternalIds = (items: TemplateGroupInstance[]) => {
            items.forEach((item) => {
              if (item.externalId) allKeys.add(item.externalId);
              collectExternalIds(item.children);
            });
          };
          collectExternalIds([group]);
          return;
        }
        findDeleted(group.children);
      }
    };
    findDeleted(groupInstances);
    setGroupInstances((current) => removeGroupInstance(current, key));
    setProductRows((current) =>
      current.filter(
        (row) => !row.groupIds?.some((groupId) => allKeys.has(String(groupId))),
      ),
    );
  };

  const renderProductTable = (
    table: ProposeTemplateTable,
    pathInstances: TemplateGroupInstance[] = [],
  ): ReactNode => {
    const fields = productFields.filter(
      (field) => !table.fieldIds?.length || table.fieldIds.includes(field.id),
    );
    const rows = productRows.filter((row) => {
      if (row.tableId !== table.id) return false;
      return pathInstances.every((group, index) => {
        const rowGroupId = row.groupIds?.[index];
        return (
          (rowGroupId === undefined && group.initial) ||
          String(rowGroupId) === String(group.externalId ?? group.key)
        );
      });
    });

    return (
      <Card
        key={`${table.id}-${pathInstances.map((group) => group.key).join("-")}`}
        size="small"
        title={table.label}
        extra={
          <Button
            icon={<PlusOutlined />}
            onClick={() => addProductRow(table, pathInstances)}
          >
            行を追加
          </Button>
        }
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          {rows.map((row, rowIndex) => (
            <Card
              key={row.key}
              size="small"
              title={`${table.label} ${rowIndex + 1}`}
              extra={
                <Button
                  danger
                  type="text"
                  aria-label={`${table.label} ${rowIndex + 1}を削除`}
                  icon={<DeleteOutlined />}
                  onClick={() =>
                    setProductRows((current) =>
                      current.filter((item) => item.key !== row.key),
                    )
                  }
                />
              }
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                  gap: "0 16px",
                }}
              >
                {fields.map((field) => (
                  <Form.Item
                    key={field.id}
                    label={field.label}
                    required={field.required}
                  >
                    <TemplateFieldInput
                      field={field}
                      value={row.values[field.id]}
                      onChange={(value) =>
                        updateProductValue(row.key, field.id, value)
                      }
                    />
                  </Form.Item>
                ))}
              </div>
            </Card>
          ))}
          {rows.length === 0 && (
            <Text type="secondary">商品行はありません。</Text>
          )}
        </Space>
      </Card>
    );
  };

  function renderGroupInstance(
    definition: ProposeTemplateRepeatableGroup,
    instance: TemplateGroupInstance,
    ancestorDefinitions: ProposeTemplateRepeatableGroup[] = [],
    ancestorInstances: TemplateGroupInstance[] = [],
  ): ReactNode {
    const currentDefinitions = [...ancestorDefinitions, definition];
    const currentInstances = [...ancestorInstances, instance];
    const currentGroupPath = currentDefinitions.map((group) => group.id);
    const matchingTables = tables.filter((table) => {
      const tablePath = table.groupPath ?? [];
      return (
        tablePath.length === currentGroupPath.length &&
        tablePath.every((groupId, index) => groupId === currentGroupPath[index])
      );
    });

    return (
      <Card
        key={instance.key}
        size="small"
        title={definition.label}
        extra={
          <Button
            danger
            type="text"
            aria-label={`${definition.label}を削除`}
            icon={<DeleteOutlined />}
            onClick={() => deleteGroup(instance.key)}
          />
        }
      >
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          {!!definition.fields?.length && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: "0 16px",
              }}
            >
              {definition.fields.map((field) => {
                const fieldValue = field.headerFieldId
                  ? headerValues[field.headerFieldId] ?? field.defaultValue ?? ""
                  : instance.values[field.id];
                return (
                  <Form.Item
                    key={field.id}
                    label={field.label}
                    required={field.required}
                  >
                    <TemplateFieldInput
                      field={field}
                      value={fieldValue}
                      onChange={(value) =>
                        updateGroupValue(instance.key, field, value)
                      }
                    />
                  </Form.Item>
                );
              })}
            </div>
          )}

          {matchingTables.map((table) =>
            renderProductTable(table, currentInstances),
          )}

          {(definition.children ?? []).map((childDefinition) => {
            const childInstances = instance.children.filter(
              (child) => child.groupId === childDefinition.id,
            );
            return (
              <Card
                key={`${instance.key}-${childDefinition.id}`}
                size="small"
                title={childDefinition.label}
                extra={
                  <Button
                    icon={<PlusOutlined />}
                    onClick={() => addGroup(instance.key, childDefinition)}
                  >
                    追加
                  </Button>
                }
              >
                <Space direction="vertical" style={{ width: "100%" }}>
                  {childInstances.map((child) =>
                    renderGroupInstance(
                      childDefinition,
                      child,
                      currentDefinitions,
                      currentInstances,
                    ),
                  )}
                  {childInstances.length === 0 && (
                    <Text type="secondary">項目はありません。</Text>
                  )}
                </Space>
              </Card>
            );
          })}
        </Space>
      </Card>
    );
  }

  const columns: ColumnsType<ProposeTemplate> = [
    {
      title: "テンプレート名",
      dataIndex: "name",
      key: "name",
      render: (name: string, template) => (
        <Space>
          <span>{name}</span>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            aria-label={`${name}の名前を変更`}
            onClick={() => {
              setRenamingTemplate(template);
              setRenameValue(template.name);
              setRenameError(null);
              setPageSuccess(null);
            }}
          />
        </Space>
      ),
    },
    {
      title: "状態",
      dataIndex: "status",
      key: "status",
      width: 130,
      render: (status: ProposeTemplate["status"]) => statusLabel(status),
    },
    {
      title: "エラー",
      key: "error",
      render: (_, template) =>
        templateErrors[template.id] ? (
          <Text type="danger">{templateErrors[template.id]}</Text>
        ) : null,
    },
    {
      key: "action",
      width: 230,
      render: (_, template) => (
        <Space size="small">
          <Button
            type="link"
            disabled={template.status !== 1}
            onClick={() => setPreviewId(template.id)}
          >
            Excelプレビュー
          </Button>
          <Popconfirm
            title="テンプレートを削除しますか？"
            description="一覧から非表示になります。過去の注文データとExcelは保持されます。"
            okText="削除"
            cancelText="キャンセル"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleDeleteTemplate(template)}
          >
            <Button type="link" danger>
              削除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <main className="propose-page">
      <PageHeader
        title="テンプレート管理"
        description="Google Sheetsからテンプレートを作成し、共有設定を管理します。"
      />

      <GoogleSheetCreateCard
        onCreated={() => setGoogleSheetsRefreshKey((key) => key + 1)}
        onTemplateCreated={refreshGoogleSheetTemplateList}
      />
      <GoogleSheetList
        refreshKey={googleSheetsRefreshKey}
        onUpdated={() => setGoogleSheetsRefreshKey((key) => key + 1)}
        onTemplateCreated={handleGoogleSheetTemplateSynced}
        onTemplateRenamed={handleGoogleSheetRenamed}
      />

      {pageSuccess && (
        <Alert
          type="success"
          showIcon
          message={pageSuccess}
          closable
          onClose={() => setPageSuccess(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      {pageError && (
        <Alert
          type="error"
          showIcon
          message={pageError}
          closable
          onClose={() => setPageError(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      {selectedId !== null && (
        <Card
          title={`注文フォーム：${templates.find((item) => item.id === selectedId)?.name ?? selectedId}`}
          bordered={false}
          style={{ marginTop: 20 }}
        >
          {formError ? (
            <Alert type="error" showIcon message={formError} />
          ) : formLoading ? (
            <Spin />
          ) : templateForm ? (
            <Space direction="vertical" size="large" style={{ width: "100%" }}>
              {!!templateForm.headerFields?.length && (
                <Card size="small" title="注文情報">
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                      gap: "0 16px",
                    }}
                  >
                    {templateForm.headerFields.map((field) => (
                      <Form.Item
                        key={field.id}
                        label={field.label}
                        required={field.required}
                      >
                        <TemplateFieldInput
                          field={field}
                          value={headerValues[field.id]}
                          onChange={(value) =>
                            setHeaderValues((current) => ({
                              ...current,
                              [field.id]: value,
                            }))
                          }
                        />
                      </Form.Item>
                    ))}
                  </div>
                </Card>
              )}

              {tables
                .filter((table) => !(table.groupPath?.length))
                .map((table) => renderProductTable(table))}

              {(templateForm.repeatableGroups ?? []).map((groupDefinition) => {
                const instances = groupInstances.filter(
                  (instance) => instance.groupId === groupDefinition.id,
                );
                return (
                  <Card
                    key={groupDefinition.id}
                    size="small"
                    title={groupDefinition.label}
                    extra={
                      <Button
                        icon={<PlusOutlined />}
                        onClick={() => addGroup(null, groupDefinition)}
                      >
                        追加
                      </Button>
                    }
                  >
                    <Space direction="vertical" style={{ width: "100%" }}>
                      {instances.map((instance) =>
                        renderGroupInstance(groupDefinition, instance),
                      )}
                      {instances.length === 0 && (
                        <Text type="secondary">項目はありません。</Text>
                      )}
                    </Space>
                  </Card>
                );
              })}
            </Space>
          ) : null}
        </Card>
      )}

      <Modal
        title="テンプレート名を変更"
        open={renamingTemplate !== null}
        onCancel={() => setRenamingTemplate(null)}
        onOk={() => void handleRenameTemplate()}
        okText="保存"
        cancelText="キャンセル"
        confirmLoading={renameSaving}
        destroyOnClose
      >
        <Space direction="vertical" size="small" style={{ width: "100%" }}>
          <label htmlFor="propose-template-rename">テンプレート名</label>
          <Input
            id="propose-template-rename"
            autoFocus
            maxLength={200}
            value={renameValue}
            onChange={(event) => setRenameValue(event.target.value)}
            onPressEnter={() => void handleRenameTemplate()}
            placeholder="テンプレート名を入力"
          />
          {renameError && <Alert type="error" showIcon message={renameError} />}
        </Space>
      </Modal>

      <Modal
        title={`Excelプレビュー：${preview?.name ?? templates.find((item) => item.id === previewId)?.name ?? previewId ?? ""}`}
        open={previewId !== null}
        onCancel={() => setPreviewId(null)}
        width="96vw"
        style={{ top: 20, maxWidth: 1200 }}
        footer={
          <Space>
            <Button onClick={() => setPreviewId(null)}>閉じる</Button>
            <Button
              type="primary"
              loading={previewSaving}
              disabled={!Object.keys(cellEdits).length || !preview}
              onClick={savePreviewEdits}
            >
              変更を保存
            </Button>
          </Space>
        }
        destroyOnClose
      >
          {previewError && (
            <Alert
              type="error"
              showIcon
              message={previewError}
              style={{ marginBottom: 16 }}
            />
          )}
          <Alert
            type="warning"
            showIcon
            message="セルの編集は元のExcelファイルに反映され、既存注文のプレビューやダウンロードにも影響します。"
            style={{ marginBottom: 16 }}
          />
          {previewLoading ? (
            <Spin />
          ) : (
            <Space direction="vertical" size="large" style={{ width: "100%" }}>
              {preview?.sheets.map((sheet) => (
                <Card key={sheet.name} size="small" title={sheet.name}>
                  <TemplateSheetPreview
                    sheet={sheet}
                    edits={cellEdits}
                    onCellChange={handlePreviewCellChange}
                  />
                </Card>
              ))}
            </Space>
          )}
      </Modal>
    </main>
  );
};
